// Technology team publishing: create, publish, reschedule and cancel calendar
// entries, publish and edit a guide, see who changed a calendar entry or a
// guide and roll it back (ACL 01, CAL 03, CAL 04). The API refuses all of this
// to anyone without the technology role; this page only hides it.

import { esc } from '../api';
import type { Preview } from '../notifications';
import { formatWhen } from '../notifications';
import type { CalendarEntry } from './calendar';
import type { Guide } from './guides';

export interface HistoryRow {
  version: number;
  action: string;
  at: string;
  byName: string;
}

export type RecordKind = 'entry' | 'guide';

export interface HistoryView {
  kind: RecordKind;
  id: string;
  rows: HistoryRow[];
}

const FIELDS: Array<[string, string, string]> = [
  ['title', 'Title', 'text'],
  ['reference', 'Change reference (optional)', 'text'],
  ['systems', 'Systems, separated by commas', 'text'],
  ['owner', 'Responsible owner', 'text'],
  ['scheduledStart', 'Date and time with offset, for example 2026-11-03T18:30:00+11:00', 'text'],
  ['timezone', 'Time zone, for example Australia/Sydney', 'text'],
  ['audience', 'Who is affected', 'text'],
  ['impact', 'What changes', 'text'],
  ['downtime', 'Downtime, or none', 'text'],
  ['userAction', 'What users need to do, or none', 'text'],
  ['riskRationale', 'Why this risk', 'text'],
];

export function entryFormFields(prefix: string): string {
  return `${FIELDS.map(
    ([name, label]) =>
      `<label for="${prefix}-${name}">${esc(label)}</label><input id="${prefix}-${name}" name="${name}" ${name === 'reference' ? '' : 'required'} />`,
  ).join('')}
<label for="${prefix}-risk">Risk</label>
<select id="${prefix}-risk" name="risk"><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option></select>`;
}

// Turns form values into the API body. Systems are a comma list in the form.
export function readEntryForm(values: Record<string, string>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [name] of FIELDS) {
    const v = (values[name] ?? '').trim();
    if (name === 'systems') out.systems = v.split(',').map((s) => s.trim()).filter(Boolean);
    else if (name === 'reference') {
      if (v) out.reference = v;
    } else out[name] = v;
  }
  out.risk = values.risk ?? 'low';
  return out;
}

export function renderPreview(preview: Preview | null): string {
  if (!preview) return '';
  return `<div class="preview" role="status" aria-label="Notification preview">
  <p class="tag">${esc(preview.label)}</p>
  <p><strong>${esc(preview.subject)}</strong></p>
  ${preview.lines.map((l) => `<p>${esc(l)}</p>`).join('')}
  <p class="muted">Would reach ${preview.audience.total} subscribed people: ${preview.audience.allSystems} on all systems, ${preview.audience.perSystem
    .map((p) => `${p.count} on ${esc(p.system)}`)
    .join(', ')}.</p>
</div>`;
}

export function renderErrors(errors: string[]): string {
  return errors.length ? `<ul class="errors" role="alert">${errors.map((e) => `<li>${esc(e)}</li>`).join('')}</ul>` : '';
}

export function renderHistory(kind: RecordKind, id: string, rows: HistoryRow[]): string {
  return `<table class="history"><caption>History</caption><thead><tr><th>Version</th><th>What</th><th>Who</th><th>When</th><th></th></tr></thead><tbody>${rows
    .map(
      (r, i) =>
        `<tr><td>${r.version}</td><td>${esc(r.action)}</td><td>${esc(r.byName)}</td><td>${esc(new Date(r.at).toISOString())}</td><td>${
          i < rows.length - 1
            ? `<button type="button" data-action="rollback" data-kind="${kind}" data-id="${esc(id)}" data-version="${r.version}">Put back this version</button>`
            : 'Current'
        }</td></tr>`,
    )
    .join('')}</tbody></table>`;
}

const historyFor = (history: HistoryView | null, kind: RecordKind, id: string) =>
  history && history.kind === kind && history.id === id ? renderHistory(kind, id, history.rows) : '';

function guideFields(prefix: string, g?: Guide): string {
  const steps = g ? g.steps.map((s) => s.text).join('\n') : '';
  return `<label for="${prefix}-title">Title</label><input id="${prefix}-title" name="title" value="${esc(g?.title ?? '')}" required />
<label for="${prefix}-system">System</label><input id="${prefix}-system" name="system" value="${esc(g?.system ?? '')}" required />
<label for="${prefix}-summary">Summary</label><input id="${prefix}-summary" name="summary" value="${esc(g?.summary ?? '')}" />
<label for="${prefix}-steps">Steps, one per line</label><textarea id="${prefix}-steps" name="steps" rows="5" required>${esc(steps)}</textarea>`;
}

// Turns the guide form into the API body. Steps are one per line.
export function readGuideForm(values: Record<string, string>): Record<string, unknown> {
  return {
    title: values.title,
    system: values.system,
    summary: values.summary,
    steps: (values.steps ?? '').split(/\r?\n/).map((t) => t.trim()).filter(Boolean).map((text) => ({ text })),
  };
}

// Guides published through the Hub. Each edit is a new version, and a guide
// can be put back to an earlier version without touching any other record.
function renderGuides(guides: Guide[], history: HistoryView | null): string {
  return `<section aria-labelledby="guides-title"><h2 id="guides-title">Published guides</h2>
${
  guides.length
    ? `<ul class="entries">${guides
        .map(
          (g) => `<li class="card">
  <p><strong>${esc(g.title)}</strong></p>
  <p class="muted">${esc(g.system)}</p>
  <div class="row"><button type="button" data-action="show-history" data-kind="guide" data-id="${esc(g.id)}">History</button></div>
  <details><summary>Edit</summary>
  <form data-action="edit-guide" data-id="${esc(g.id)}">${guideFields(`g-${g.id}`, g)}<button type="submit">Save guide</button></form>
  </details>
  ${historyFor(history, 'guide', g.id)}
</li>`,
        )
        .join('')}</ul>`
    : '<p class="muted">No guides published through the Hub yet.</p>'
}</section>`;
}

export function renderPublish(entries: CalendarEntry[], guides: Guide[], preview: Preview | null, errors: string[], history: HistoryView | null): string {
  return `
<h1>Publish</h1>
<p class="muted">Technology team only. Calendar entries and guides here are sample content.</p>
${renderErrors(errors)}
${renderPreview(preview)}
<section aria-labelledby="new-entry"><h2 id="new-entry">New calendar entry</h2>
<form data-action="create-entry">${entryFormFields('new')}
<label><input type="checkbox" name="publish" checked /> Publish now</label>
<button type="submit">Save entry</button></form></section>

<section aria-labelledby="entries-title"><h2 id="entries-title">Entries</h2>
<ul class="entries">${entries
    .map(
      (e) => `<li class="card">
  <p><strong>${esc(e.title)}</strong> <span class="tag">${esc(e.state)}</span>${e.published ? '' : ' <span class="tag">Not published</span>'}</p>
  <p class="muted">${esc(formatWhen(e.scheduledStart, e.timezone))} · ${esc(e.systems.join(', '))}</p>
  <div class="row">
    ${e.published ? '' : `<button type="button" data-action="publish-entry" data-id="${esc(e.id)}">Publish</button>`}
    ${
      e.state === 'cancelled'
        ? ''
        : `<form data-action="reschedule-entry" data-id="${esc(e.id)}" class="inline"><label>New date and time <input name="scheduledStart" placeholder="2026-11-05T18:30:00+11:00" required /></label><button type="submit">Reschedule</button></form>
    <form data-action="cancel-entry" data-id="${esc(e.id)}" class="inline"><label>Reason <input name="reason" /></label><button type="submit">Cancel change</button></form>`
    }
    <button type="button" data-action="show-history" data-kind="entry" data-id="${esc(e.id)}">History</button>
  </div>
  ${historyFor(history, 'entry', e.id)}
</li>`,
    )
    .join('')}</ul></section>

<section aria-labelledby="guide-title"><h2 id="guide-title">Publish a guide</h2>
<form data-action="publish-guide">
${guideFields('g')}
<button type="submit">Publish guide</button></form></section>

${renderGuides(guides, history)}`;
}
